import { useState } from "react";
import { Link } from "react-router-dom";
import { Button, CircularProgress, Typography } from "@material-ui/core";
import PostSessionQuizReview from "./post-session-quiz-review";
import { When } from "./post-session-quizzes-section";
import usePageChoice, { studentProgressLocation } from "./use-page-choice";
import { POST_SESSION, activityCoverage, mergeActivity, postSessionEvents, tagEvents, waitingCount } from "./tutor-activity";

const VISIBLE = 10;

const KIND_LABEL = {
  completed: "Completed post-session quiz",
  // A claim means the quiz was added to the student's account, not that any
  // question was answered.
  claimed: "Added post-session quiz to their account",
  flagged: "Flagged a question for you",
};
const SOURCE_LABEL = { [POST_SESSION]: "Post-session quiz", tag: "Flagged question" };
const FAILED_LABEL = { [POST_SESSION]: "post-session quizzes", tag: "flagged questions" };
const SHORT_LABEL = { [POST_SESSION]: "post-session quizzes", tag: "flagged questions" };

const joinAnd = (items) => (items.length < 2 ? items.join("") : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`);

function ActivityItem({ event, label, onReview, disabled }) {
  const name = label?.name || event.studentName || "A student";
  const reviewLabel = event.row?.state === "COMPLETED" ? "Review results" : "View quiz";
  return (
    <li className="tad-item" data-testid="activity-item">
      <div className="tad-item-main">
        <span className="tad-student">
          {event.studentUserId
            ? <Link to={(location) => studentProgressLocation(event.studentUserId, location.state)}>{name}</Link>
            : name}
          {label?.secondary && <span className="mtr-sub tad-secondary"> · {label.secondary}</span>}
        </span>
        <span className="tad-what">
          {KIND_LABEL[event.kind]}
          {event.title && <>: <strong>{event.title}</strong></>}
        </span>
        <span className="tad-meta mtr-sub">
          <span className="tad-source">{SOURCE_LABEL[event.source]}</span>
          {" · "}
          <When value={event.at} missing="Date unavailable" />
          {event.score && <> · <span className="tad-score">{Math.round(event.score.correct / event.score.total * 100)}%</span> ({event.score.correct}/{event.score.total} correct)</>}
          {event.answered && <> · {event.answered.answered}/{event.answered.total} answered so far</>}
        </span>
      </div>
      <div className="tad-actions">
        {event.source === POST_SESSION && event.row?.canReview === true && (
          <Button className="mtr-btn" variant="outlined" color="primary" size="small" disabled={disabled}
            onClick={() => onReview(event.row)} aria-label={`${reviewLabel}: ${event.title}`}>
            {reviewLabel}
          </Button>
        )}
        {/* The student's page lists their flagged questions; it does not open one. */}
        {event.source === "tag" && event.studentUserId && (
          <Link className="tad-link" aria-label={`See flagged questions for ${name}`}
            to={(location) => studentProgressLocation(event.studentUserId, location.state)}>
            See flagged questions
          </Link>
        )}
      </div>
    </li>
  );
}

/**
 * Tutor dashboard, first section: recent learning activity across the tutor's
 * students, merged from the sources the page loads once for everyone (the
 * post-session quiz list and OPEN question tags). It never fans out to
 * per-student history, and its coverage line names exactly what was loaded, so
 * it never reads as "all activity". Link opens and sends are not learning
 * activity; they stay in the post-session email tracker.
 *
 * postSession: usePostSessionQuizzes() result plus `enabled`.
 * tags: { enabled, status: "loading" | "ok" | "dark" | "error", tags, load }.
 * labels: Map studentUserId -> { name, secondary } from the directory, so a
 * duplicated name reads the same here as in the student list.
 * activeStudentIds: Set of currently linked student ids, or null while the
 * list is loading or failed. Flags from a student who is no longer linked (for
 * example right after Stop sharing) are dropped, since their page would refuse
 * the tutor; nothing is filtered while the list is unknown.
 */
export default function RecentActivitySection({ postSession, tags, labels, activeStudentIds }) {
  const [review, setReview] = useState(null);
  const [showAll, setShowAll] = usePageChoice("activityExpanded", false);
  const coverage = activityCoverage({ postSession, tags });
  if (!coverage.anySource) return null;

  const rows = postSession.enabled && postSession.data && postSession.data !== "dark" && !postSession.data.reason
    ? postSession.data.rows : [];
  const tagList = (tags.enabled && tags.status === "ok" ? tags.tags : [])
    .filter((tag) => !activeStudentIds || activeStudentIds.has(tag?.studentUserId));
  const events = mergeActivity(postSessionEvents(rows), tagEvents(tagList));
  const shown = showAll ? events : events.slice(0, VISIBLE);
  const waiting = waitingCount(rows);
  const retry = { [POST_SESSION]: postSession.load, tag: tags.load };
  const loadedShort = [
    ...(postSession.enabled && coverage.loaded.some((s) => s.startsWith("post-session")) ? [SHORT_LABEL[POST_SESSION]] : []),
    ...(tags.enabled && tags.status === "ok" ? [SHORT_LABEL.tag] : []),
  ];

  return (
    <section className="mtr-section tad" aria-labelledby="tad-title" data-testid="tad-section">
      <Typography variant="subtitle1" component="h2" className="mtr-section-title tad-title" id="tad-title">
        Recent activity
      </Typography>
      {coverage.loaded.length > 0 && (
        <Typography variant="caption" component="p" className="mtr-sub" data-testid="tad-coverage">
          From {joinAnd(coverage.loaded)}. Other quizzes are on each student’s page.
        </Typography>
      )}

      {review && <PostSessionQuizReview row={review} onClose={() => setReview(null)} />}

      {coverage.failed.map((key) => (
        <div className="mtr-error" role="alert" key={key}>
          <Typography variant="body2" component="span">
            Couldn’t load {FAILED_LABEL[key]}, so this list may be incomplete.
          </Typography>{" "}
          <Button className="mtr-btn" variant="outlined" size="small" onClick={retry[key]}>Retry</Button>
        </div>
      ))}
      {coverage.partial.map((note) => (
        <Typography key={note} variant="body2" className="mtr-sub" component="p">{note}</Typography>
      ))}

      {coverage.loading && (
        <div className="mtr-loading" role="status" aria-live="polite">
          <CircularProgress size={18} aria-hidden="true" />
          <Typography variant="body2">Loading recent activity…</Typography>
        </div>
      )}

      {coverage.allLoaded && events.length === 0 && loadedShort.length > 0 && (
        <Typography variant="body2" className="mtr-empty" data-testid="tad-empty">
          No recent learning activity from {loadedShort.join(" or ")}. Open a student below to see all of their quizzes.
        </Typography>
      )}

      {events.length > 0 && (
        <ol className="tad-list" aria-label="Recent learning activity, newest first">
          {shown.map((event) => (
            <ActivityItem key={event.key} event={event} label={event.studentUserId ? labels?.get(event.studentUserId) : null}
              onReview={setReview} disabled={!!review} />
          ))}
        </ol>
      )}
      {events.length > VISIBLE && (
        <Button className="mtr-btn" size="small" color="inherit" aria-expanded={showAll}
          onClick={() => setShowAll(!showAll)}>
          {showAll ? "Show less activity" : `Show ${events.length - VISIBLE} more`}
        </Button>
      )}

      {waiting > 0 && (
        <Typography variant="caption" component="p" className="mtr-sub" data-testid="tad-waiting">
          {waiting === 1 ? "1 post-session quiz sent but not started" : `${waiting} post-session quizzes sent but not started`}
          {" "}— see Post-session quiz emails below.
        </Typography>
      )}
    </section>
  );
}
